import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Trophy, ChevronRight, Wand2, ArrowRight, Search } from "lucide-react";
import { magicStyles, magicChallenge } from "@/lib/magicApi";

// Category → vibrant accent + emoji-free svg icon glyph
const CAT_META = {
  "funny":    { label: "Funny",    color: "#FFD700", tint: "rgba(255,215,0,0.14)" },
  "magical":  { label: "Magical",  color: "#B47BFF", tint: "rgba(180,123,255,0.16)" },
  "sci-fi":   { label: "Sci-Fi",   color: "#00F0FF", tint: "rgba(0,240,255,0.14)" },
  "cool":     { label: "Cool",     color: "#FF00E5", tint: "rgba(255,0,229,0.16)" },
  "art":      { label: "Art",      color: "#FF7A00", tint: "rgba(255,122,0,0.14)" },
  "animals":  { label: "Animals",  color: "#39FF14", tint: "rgba(57,255,20,0.14)" },
};

export default function MagicHome() {
  const [cats, setCats] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const { status } = useOutletContext() || {};

  useEffect(() => {
    magicStyles().then((r) => setCats(r.categories || [])).catch(() => toast.error("Couldn't load styles"));
    magicChallenge().then(setChallenge).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return cats
      .filter((c) => activeCat === "all" || c.category === activeCat)
      .map((c) => ({
        ...c,
        styles: c.styles.filter((s) => !term || s.name.toLowerCase().includes(term)),
      }))
      .filter((c) => c.styles.length > 0);
  }, [cats, q, activeCat]);

  return (
    <div className="space-y-6" data-testid="magic-home">
      <Hero />

      {challenge && (
        <ChallengeBanner challenge={challenge} completed={status?.challenge_completed} />
      )}

      {/* Search + filter chips */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="style-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search 49 styles…"
            className="w-full pl-10 pr-3 py-2.5 rounded-full bg-[#150525] border border-[#7A00FF]/30 focus:border-[#FF00E5]/60 outline-none text-sm placeholder-white/40" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="cat-chips">
          <Chip active={activeCat === "all"} onClick={() => setActiveCat("all")} color="#FF00E5" testId="cat-chip-all">All</Chip>
          {cats.map((c) => {
            const meta = CAT_META[c.category] || { label: c.category, color: "#FF00E5" };
            return (
              <Chip key={c.category} active={activeCat === c.category} onClick={() => setActiveCat(c.category)}
                color={meta.color} testId={`cat-chip-${c.category}`}>
                {meta.label}
              </Chip>
            );
          })}
        </div>
      </div>

      {/* Style grid, grouped by category */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-white/50" data-testid="styles-empty">No styles match "{q}".</div>
      ) : (
        <div className="space-y-6" data-testid="style-groups">
          {filtered.map((c) => {
            const meta = CAT_META[c.category] || { label: c.category, color: "#FF00E5", tint: "rgba(255,0,229,0.1)" };
            return (
              <section key={c.category} data-testid={`cat-${c.category}`}>
                <div className="flex items-center mb-3">
                  <h2 className="font-black text-xl">{meta.label}</h2>
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-white/40">{c.styles.length} styles</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                  {c.styles.map((s) => <StyleTile key={s.slug} style={s} accent={meta.color} tint={meta.tint} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Hero() {
  return (
    <section data-testid="magic-hero" className="relative overflow-hidden rounded-3xl border border-[#FF00E5]/30 bg-gradient-to-br from-[#FF00E5]/25 via-[#7A00FF]/25 to-[#00F0FF]/20 p-6 sm:p-8">
      <div className="absolute -top-14 -right-14 w-52 h-52 rounded-full blur-3xl bg-[#FF00E5]/40" />
      <div className="absolute -bottom-16 -left-16 w-52 h-52 rounded-full blur-3xl bg-[#00F0FF]/25" />
      <div className="relative">
        <div className="inline-flex items-center gap-1.5 bg-black/40 border border-[#FF00E5]/40 text-[#FF00E5] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
          <Sparkles className="w-3 h-3" fill="currentColor" /> Jixxeo Magic
        </div>
        <h1 className="font-black text-3xl sm:text-5xl leading-none">Turn a selfie<br/>into <span className="text-[#FF00E5] drop-shadow-[0_0_18px_rgba(255,0,229,0.7)]">anything.</span></h1>
        <p className="text-sm sm:text-base text-white/70 mt-3 max-w-md">
          Upload a photo, pick a style — wizard, cyberpunk, dragon, LEGO, oil painting — and Jixxeo Magic transforms you in seconds.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/magic#styles" data-testid="hero-browse-btn" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 340, behavior: 'smooth' }); }}
            className="inline-flex items-center gap-1 bg-[#FF00E5] text-white font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full shadow-[0_0_20px_rgba(255,0,229,0.5)]">
            <Wand2 className="w-3.5 h-3.5" /> Browse styles
          </Link>
          <Link to="/magic/gallery" data-testid="hero-gallery-btn"
            className="inline-flex items-center gap-1 border border-white/20 bg-white/5 text-white font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
            My gallery <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ChallengeBanner({ challenge, completed }) {
  return (
    <Link to={`/magic/create/${challenge.style_slug}?daily=1`} data-testid="challenge-banner"
      className="block rounded-2xl border border-[#FFD700]/50 bg-gradient-to-r from-[#FFD700]/20 via-[#FF00E5]/15 to-transparent p-4 hover:from-[#FFD700]/30 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#FFD700] text-[#0A0512] flex items-center justify-center shrink-0">
          <Trophy className="w-6 h-6" fill="currentColor" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#FFD700] font-bold">Daily Magic Challenge · +{challenge.bonus_coins} coins</div>
          <div className="font-black text-base sm:text-lg truncate">Today's style: <span className="text-[#FFD700]">{challenge.style_name}</span></div>
          <div className="text-[11px] text-white/60">{completed ? "You already won today's bonus 🏆" : "Be the first of your friends to try it."}</div>
        </div>
        <ChevronRight className="w-5 h-5 text-white/40 shrink-0" />
      </div>
    </Link>
  );
}

function Chip({ active, onClick, color, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={"shrink-0 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border transition-colors " +
        (active ? "border-transparent text-black" : "border-white/10 text-white/70 hover:border-white/30 bg-white/5")}
      style={active ? { background: color } : {}}>
      {children}
    </button>
  );
}

function StyleTile({ style, accent, tint }) {
  return (
    <Link to={`/magic/create/${style.slug}`} data-testid={`style-tile-${style.slug}`}
      className="group relative aspect-square rounded-2xl border border-white/10 hover:border-current transition-colors overflow-hidden"
      style={{ color: accent, backgroundImage: `linear-gradient(135deg, ${tint} 0%, rgba(10,5,18,0.9) 100%)` }}>
      <div className="absolute inset-0 flex flex-col justify-end p-3">
        <div className="text-white font-black text-sm leading-tight">{style.name}</div>
        <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">Tap to create</div>
      </div>
      <div className="absolute top-2 right-2 opacity-40 group-hover:opacity-100 transition-opacity">
        <Sparkles className="w-4 h-4" fill="currentColor" />
      </div>
    </Link>
  );
}
